import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import {
    AnimatedPageLoader,
    icons,
    InfoAlert,
    CollapsiblePanel,
    PageActionMenu,
    CollapsiblePanelBody,
    CollapsiblePanelHeader,
} from 'smartsuite-ui';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useSortable } from '../shared/hooks/useSortable';
import { metricDataPromise } from '../viewDashboards/utils/fetchUtils';
import ConfigurationModal from './ConfigurationModal';
import ShareModal from './sharingAndSaving/ShareModal';
import FiltersContainer from './FiltersContainer';
import { VirtuosoTable } from './Table/VirtuosoTable';
import SavedFiltersContainer from './sharingAndSaving/SavedFiltersContainer';
import {
    formatFiltersForBackend,
    formatWhereClausesForBackend,
    mergeRecordLimit,
} from '../viewDashboards/utils/metricRequestUtils';
import ManageSavedDashboardsModal from '../viewDashboards/sharingAndSaving/ManageSavedDashboardsModal';
import buildRollupText from '../shared/filtering/rollupTextUtils';

function applyFiltersToMetricRequest(
    baseMetricRequest,
    searchTimeAndPrecision,
    searchFilterValues,
    searchWhereClause,
    searchRecordLimit
) {
    const timeAndPrecision = { ...searchTimeAndPrecision };
    const filters = { ...searchFilterValues, ...baseMetricRequest.filters };
    const where = [...searchWhereClause, ...baseMetricRequest.where];
    const recordLimit = mergeRecordLimit(searchRecordLimit, baseMetricRequest);

    return {
        ...baseMetricRequest,
        timeAndPrecision,
        filters: JSON.stringify(formatFiltersForBackend(filters)),
        where: formatWhereClausesForBackend(where),
        recordLimit: recordLimit.recordLimit,
        isDescending: recordLimit.isDescending,
    };
}

function mergeDataSources(metricSources, groupBys) {
    if (!groupBys?.length) {
        return metricSources[0].metricSeries;
    }

    const sourcesWithDataKeyedByGroups = metricSources.map((metricSource) =>
        _.keyBy(metricSource.metricSeries, (item) =>
            groupBys.map((groupBy) => `${item[groupBy]}`).join(';')
        )
    );

    return _({})
        .merge(...sourcesWithDataKeyedByGroups)
        .values()
        .value();
}

export default function SearchApp(props) {
    const { t } = useTranslation('dashboard');
    const dashboardLabels = t('dashboardLabels', { returnObjects: true });

    const { searchKey } = useParams();

    const [metricData, setMetricData] = useState({});
    const [openModal, setOpenModal] = useState(false);
    const [tableConfig, setTableConfig] = useState(
        props.configuration.tableConfiguration.columns
    );

    const [isManageSavedVisible, setIsManageSavedVisible] = useState(false);
    const [filterValues, setFilterValues] = useState(
        props.configuration.filterValues
    );
    const [timeAndPrecision, setTimeAndPrecision] = useState(
        props.configuration.timeAndPrecision
    );
    const [whereClauses, setWhereClauses] = useState(
        props.configuration.whereClauses
    );
    const [recordLimit, setrecordLimit] = useState({
        recordLimit: props.configuration.recordLimit,
        isDescending: props.configuration.isDescending,
    });
    const showPrecisionConfig = false;

    const [filtersCollapsed, setIsFiltersCollapsed] = useState(false);
    useEffect(() => {
        setFilterValues(props.configuration.filterValues);
        setWhereClauses(props.configuration.whereClauses);
        setTimeAndPrecision(props.configuration.timeAndPrecision);
        setrecordLimit({
            recordLimit: props.configuration.recordLimit,
            isDescending: props.configuration.isDescending,
        });
    }, [props.configuration]);

    function onSaveModel(newTableConfig) {
        setTableConfig(newTableConfig);
    }

    function toggleFiltersCollapsed() {
        setIsFiltersCollapsed((prevState) => !prevState);
    }

    const [isShareModalVisible, setIsShareModalVisible] = useState(false);
    const [hasSearchedOnce, setHasSearchedOnce] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const pageFiltersRef = useRef();
    const defaultTableConfiguration =
        props.configuration.tableConfiguration.columns;

    function renderManageSavedDashboards() {
        return (
            isManageSavedVisible && (
                <ManageSavedDashboardsModal
                    deleteSavedDashboard={props.deleteSavedSearch}
                    onDismiss={() => setIsManageSavedVisible(false)}
                    pageTitle={props.configuration.title}
                    savedDashboardError="test error"
                    savedDashboardNames={props.savedSearchNames}
                    siteMapKey={props.configuration.siteMapKey}
                />
            )
        );
    }

    function onSaveSearch(name, isSaveNew) {
        const updatedFilterState = {
            timeAndPrecision,
            filterValues,
            whereClauses,
            recordLimit: recordLimit.recordLimit,
            isDescending: !!recordLimit.isDescending,
        };

        props.saveSearch(name, isSaveNew, updatedFilterState);
    }

    function renderShareModal() {
        const unappliedFiltersExist = pageFiltersRef.current
            ? pageFiltersRef.current.unappliedFiltersExist()
            : false;

        return (
            isShareModalVisible && (
                <ShareModal
                    configurationForShare={{
                        timeRange: {
                            endDateTime: '2023-03-01 00:00',
                            rangeType: 'day',
                            rangeValue: 30,
                            startDateTime: '2015-02-28 00:00',
                        },
                    }}
                    advancedSearchKey={searchKey ?? ''}
                    onDismiss={() => setIsShareModalVisible(false)}
                    unappliedFiltersExist={unappliedFiltersExist}
                    uriConstants={props.uriConstants}
                />
            )
        );
    }

    const removedHiddenColumns = tableConfig.filter((item) => !item.isHidden);
    const fetchMetricData = () => {
        setIsSearching(true);
        const metricRequests = props.configuration.metricRequests.map(
            (baseRequest) =>
                applyFiltersToMetricRequest(
                    baseRequest,
                    timeAndPrecision,
                    filterValues,
                    whereClauses,
                    recordLimit
                )
        );
        metricDataPromise(props.uriConstants.metricDataUri, { metricRequests })
            .then(
                (response) => {
                    setHasSearchedOnce(true);
                    setMetricData(
                        mergeDataSources(response, metricRequests[0].groupBy)
                    );
                },
                // eslint-disable-next-line no-alert
                (err) => alert(err) // TODO: handle error
            )
            .then(() => setIsSearching(false));
    };
    const [sortObject, setSort, sortedResults] = useSortable(metricData, {
        columnName: 'flight',
        sortOrder: 'desc',
    });

    function getPageActions(isRequiredFilters = false) {
        const pageActions = [];

        if (props.configuration.isShareEnabled) {
            pageActions.push({
                description: dashboardLabels.shareDashboard,
                iconClass: icons.share,
                sortOrder: 1,
                onClick: () => {
                    setIsShareModalVisible(true);
                },
                isDisabled: isRequiredFilters,
            });
        }

        pageActions.push({
            description: 'Settings',
            iconClass: icons.cog,
            onClick: () => {},
            subActions: [
                {
                    description: dashboardLabels.manageSavedFilters,
                    iconClass: icons.cog,
                    sortOrder: 2,
                    onClick: () => {
                        setIsManageSavedVisible(true);
                    },
                },
                {
                    description: 'Table Configurations',
                    iconClass: icons.cog,
                    sortOrder: 3,
                    onClick: () => {
                        setOpenModal(true);
                    },
                },
            ],
        });

        return pageActions;
    }

    function renderTable() {
        if (!hasSearchedOnce) {
            return null;
        }

        if (isSearching) {
            return <AnimatedPageLoader />;
        }

        return (
            <VirtuosoTable
                data={sortedResults}
                columns={removedHiddenColumns}
                labels={{}}
                sortObject={sortObject}
                onSort={setSort}
            />
        );
    }

    const requiredFilterKeys = Object.keys(
        _.pickBy(
            props.configuration.filterConfigurations,
            (filter) => filter.isRequired
        )
    );

    const rollupText = buildRollupText(
        timeAndPrecision,
        filterValues,
        whereClauses,
        props.configuration.timeAndPrecisionConfiguration.showTimeAndPrecision,
        showPrecisionConfig,
        props.configuration.whereClauseConfiguration.showMeasureFilters,
        props.configuration.filterConfigurations,
        requiredFilterKeys,
        props.selectOptions,
        dashboardLabels
    );

    return (
        <div className="advanced-search">
            <div className="advanced-search__header">
                <div className="advanced-search__header-title">
                    {props.configuration.searchTitle}
                </div>
                <div className="advanced-search__header-pageactions">
                    <PageActionMenu
                        pageHelpUrl={props.uriConstants.pageHelpUri(
                            props.configuration.onlineHelpId
                        )}
                        pageActions={getPageActions()}
                    />
                </div>
            </div>
            <div className="advanced-search__filters">
                <CollapsiblePanel
                    isCollapsed={filtersCollapsed}
                    onCollapse={toggleFiltersCollapsed}
                >
                    <CollapsiblePanelHeader
                        autoHideRollupText={false}
                        headerLabel="Advanced-Search Filters"
                        rollupText={rollupText}
                    >
                        <SavedFiltersContainer
                            isSaveDisabled={false}
                            isSavedFilterModified={false}
                            loadSavedSearch={props.loadSavedSearch}
                            savedSearchNames={props.savedSearchNames}
                            savedFilterNames={props.savedSearchNames}
                            selectedValue={props.configuration}
                            siteMapKey={props.configuration.siteMapKey}
                            onSave={onSaveSearch}
                        />
                    </CollapsiblePanelHeader>
                    <CollapsiblePanelBody>
                        <FiltersContainer
                            filterConfigurations={
                                props.configuration.filterConfigurations
                            }
                            filterValues={filterValues}
                            selectOptions={props.selectOptions}
                            saveFilters={setFilterValues}
                            onSearch={fetchMetricData}
                            timeAndPrecisionConfiguration={
                                props.configuration
                                    .timeAndPrecisionConfiguration
                            }
                            saveTimeAndPrecision={setTimeAndPrecision}
                            timeAndPrecision={timeAndPrecision}
                            whereClauseConfiguration={
                                props.configuration.whereClauseConfiguration
                            }
                            saveWhereClauses={setWhereClauses}
                            whereClauses={whereClauses}
                            showRecordLimit={
                                props.configuration.showRecordLimit
                            }
                            recordLimit={recordLimit}
                            saveRecordLimit={setrecordLimit}
                        />
                    </CollapsiblePanelBody>
                </CollapsiblePanel>
            </div>
            <div className="advanced-search__search-criteria" />
            <div className="advanced-search_info-message">
                {hasSearchedOnce && (
                    <InfoAlert>
                        Search returned {metricData.length} results
                    </InfoAlert>
                )}
            </div>
            <div className="advanced-search__table">{renderTable()}</div>
            {openModal && (
                <ConfigurationModal
                    closeModal={() => setOpenModal(false)}
                    onConfirm={onSaveModel}
                    colunmConfig={tableConfig}
                    defaultTableConfig={defaultTableConfiguration}
                />
            )}
            {renderManageSavedDashboards()}
            {renderShareModal()}
        </div>
    );
}

SearchApp.propTypes = {
    uriConstants: PropTypes.instanceOf(Object).isRequired,
    deleteSavedSearch: PropTypes.func,
    savedSearchNames: PropTypes.instanceOf(Array),
    selectOptions: PropTypes.instanceOf(Array).isRequired,
    saveSearch: PropTypes.func,
    loadSavedSearch: PropTypes.func,
    configuration: PropTypes.instanceOf(Object).isRequired,
    onlineHelpId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SearchApp.defaultProps = {
    saveSearch: () => null,
};

SearchApp.defaultProps = {
    deleteSavedSearch: () => null,
    savedSearchNames: [],
    onlineHelpId: undefined,
};
